const aiChatBotServicesData = [
  {
    title: "Conversational AI Chatbot",
    description:
      "Build intelligent chatbots that understand user intent and deliver natural, context-aware responses across channels.",
    bullets: [
      "Intent Understanding",
      "Contextual Replies",
      "Fast Response",
      "Custom Flows",
    ],
    minHeight: 420,
  },
  {
    title: "Multi-Channel Support",
    description:
      "Deploy a single chatbot across web, mobile, WhatsApp, and Messenger with unified conversation state and analytics.",
    bullets: [
      "Web & Mobile",
      "WhatsApp & Messenger",
      "Unified State",
      "Seamless Handoffs",
    ],
    minHeight: 420,
  },
  {
    title: "Knowledge Base Integration",
    description:
      "Connect your existing FAQs, docs, and help center to enable instant, accurate answers without manual routing.",
    bullets: ["FAQ Sync", "Doc Search", "Auto-Answers", "CMS Connectors"],
    minHeight: 420,
  },
  {
    title: "NLU Training & Tuning",
    description:
      "Improve accuracy with curated training data, active learning, and continuous model tuning for your domain.",
    bullets: [
      "Custom Intents",
      "Entity Extraction",
      "Active Learning",
      "Model Monitoring",
    ],
    minHeight: 420,
  },
  {
    title: "Custom Workflows & Actions",
    description:
      "Design tailored conversation flows that trigger backend actions like bookings, lookups, and payments.",
    bullets: [
      "Backend Actions",
      "API Integrations",
      "Conditional Flows",
      "Secure Transactions",
    ],
    minHeight: 420,
  },
  {
    title: "Live Agent Handoff",
    description:
      "Seamlessly escalate complex conversations to human agents with full context and transcripts.",
    bullets: [
      "Contextual Handoff",
      "Transcripts",
      "Agent Tools",
      "Priority Routing",
    ],
    minHeight: 420,
  },
  {
    title: "Analytics & Insights",
    description:
      "Monitor conversations, identify trends, and optimise bot performance with dashboards and reports.",
    bullets: [
      "Conversation Metrics",
      "Funnel Analysis",
      "Custom Dashboards",
      "Exportable Reports",
    ],
    minHeight: 420,
  },
  {
    title: "Multilingual Support",
    description:
      "Reach global customers with automatic language detection and localized responses powered by translation layers.",
    bullets: [
      "Auto-detect Language",
      "Localized Content",
      "Translation Pipeline",
      "Regional Tuning",
    ],
    minHeight: 420,
  },
  {
    title: "Deployment & Monitoring",
    description:
      "Robust deployment options with CI/CD, uptime monitoring, logging, and alerting to keep your bot reliable.",
    bullets: [
      "CI/CD",
      "Uptime Monitoring",
      "Logging & Alerts",
      "Rollback Support",
    ],
    minHeight: 420,
  },
];

export default aiChatBotServicesData;

// Centralized content for sections (optional for components to import)
export const aiChatBotBlueprintNodes = [
  {
    id: "conversational",
    label: "Advanced Conversational AI",
    title: "Advanced Conversational AI Core",
    desc: "At the heart of our platform is a proprietary Natural Language Processing (NLP) engine. It goes beyond keywords to understand context, sentiment, and user intent, enabling fluid, human-like dialogue that solves real problems.",
    tech: [
      "Intent Recognition",
      "Sentiment Analysis",
      "Context-Aware Dialogue",
      "Multi-Language Support",
    ],
  },
  {
    id: "integration",
    label: "Unified Integration Fabric",
    title: "Unified Integration Fabric",
    desc: "Your chatbot doesn't operate in a silo. Our integration fabric provides seamless, bi-directional connectivity to your essential business systems, including CRMs, ERPs, and APIs, creating a fully automated workflow engine.",
    tech: [
      "CRM & ERP Sync",
      "REST API Connectivity",
      "Third-Party Webhooks",
      "Workflow Automation",
    ],
  },
  {
    id: "learning",
    label: "Continuous Learning Loop",
    title: "Continuous Learning Loop",
    desc: "Our AI is designed to grow with your business. It utilizes machine learning models that analyze conversations in real-time, identifying patterns and optimizing responses. Your chatbot gets smarter and more efficient with every interaction.",
    tech: [
      "Machine Learning Models",
      "Interaction Analysis",
      "Automated A/B Testing",
      "Performance Optimization",
    ],
  },
  {
    id: "security",
    label: "Enterprise-Grade Security & Scale",
    title: "Enterprise-Grade Security & Scale",
    desc: "Built on a resilient cloud architecture, our platform is engineered for mission-critical performance. We ensure 99.9% uptime, end-to-end data encryption, and full compliance with global standards, ready to handle millions of conversations.",
    tech: [
      "End-to-End Encryption",
      "GDPR & CCPA Compliant",
      "99.9% Uptime SLA",
      "Auto-Scaling Infrastructure",
    ],
  },
];

export const aiChatBotPhases = [
  {
    id: "discovery",
    title: "Discovery & Strategy",
    desc: "Our journey begins with a deep dive into your unique business goals. We collaborate with your team to understand your challenges, define KPIs, and map the ideal conversational flows.",
    bullets: [
      "AI Readiness Assessment",
      "Detailed Project Roadmap",
      "Persona & Use-Case Definition",
    ],
    side: "left",
  },
  {
    id: "design",
    title: "Design & Integration",
    desc: "We construct your AI's custom knowledge base and personality, then integrate it with your technology stack—CRMs, APIs and internal systems for a perfect fit.",
    bullets: [
      "Custom Knowledge Base",
      "Conversation Flow Blueprint",
      "API & CRM Integration",
    ],
    side: "right",
  },
  {
    id: "testing",
    title: "Testing & Deployment",
    desc: "Before going live, your AI undergoes rigorous testing in a secure sandbox. We simulate thousands of interactions and then execute a flawless deployment.",
    bullets: [
      "User Acceptance Testing (UAT)",
      "Security & Performance Audit",
      "Go-Live Launch Sequence",
    ],
    side: "left",
  },
  {
    id: "optimization",
    title: "Optimization & Scale",
    desc: "Launch is just the beginning. Continuous learning, analytics and ongoing tuning ensure the AI improves and scales with your business.",
    bullets: [
      "Live Performance Dashboard",
      "Monthly Insight Reports",
      "Ongoing Model Tuning",
    ],
    side: "right",
  },
];

export const aiChatBotPerformanceCategories = [
  {
    id: "growth",
    title: "Business Growth Engine",
    subtitle: "(Revenue, leads & conversion)",
    metrics: [
      {
        id: "qlv",
        title: "Qualified Lead Velocity",
        value: "+40%",
        type: "percent",
        desc: "Increase in the number of sales-qualified leads generated through the website per month.",
        numeric: 40,
      },
      {
        id: "cfa",
        title: "Conversion Funnel Acceleration",
        value: "2.5x Faster",
        type: "multiplier",
        desc: "Visitors who engage with the AI chatbot convert 2.5 times faster than those who don't.",
        numeric: 2.5,
      },
      {
        id: "arpc",
        title: "Average Revenue Per Conversation",
        value: "$12.40",
        type: "currency",
        desc: "The average attributable revenue generated from each successfully completed chatbot conversation.",
        numeric: 12.4,
      },
    ],
  },
  {
    id: "ops",
    title: "Operational Command Center",
    subtitle: "(Efficiency, cost savings & automation)",
    metrics: [
      {
        id: "arr",
        title: "Automated Resolution Rate",
        value: "75%",
        type: "percent",
        desc: "Percentage of inbound user queries that are fully resolved by the AI without any human intervention.",
        numeric: 75,
      },
      {
        id: "frt",
        title: "First Response Time",
        value: "< 1 Second",
        type: "time",
        desc: "The time it takes for a user to get their first meaningful response, eliminating wait times completely.",
        numeric: 1,
      },
      {
        id: "ocr",
        title: "Operational Cost Reduction",
        value: "-65%",
        type: "percent-neg",
        desc: "The average reduction in customer support operational costs within the first six months of implementation.",
        numeric: 65,
      },
    ],
  },
  {
    id: "intel",
    title: "Customer Intelligence Core",
    subtitle: "(Data, satisfaction & insights)",
    metrics: [
      {
        id: "csat",
        title: "Predictive CSAT Score",
        value: "9.1 / 10",
        type: "score",
        desc: "Our AI's real-time sentiment analysis predicts customer satisfaction with over 90% accuracy.",
        numeric: 9.1,
      },
      {
        id: "intent",
        title: "Real-Time Intent Recognition",
        value: "98% Accuracy",
        type: "percent",
        desc: 'The AI correctly identifies the user\'s primary goal or "intent" in 98% of conversations.',
        numeric: 98,
      },
      {
        id: "insights",
        title: "Actionable Insights Generated",
        value: "500+/month",
        type: "count",
        desc: "Average number of actionable data points captured by the AI for business intelligence.",
        numeric: 500,
      },
    ],
  },
];
